import { useEffect, useState } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import { capitalize } from 'utils'
import { Alert, LookupField, RegistrationLookupWrapper } from 'frame/components'
import { createDefaultModel } from '../../module'
import { hasRoles } from 'acl'
import { useSelectedFleets } from 'features/settings'
import { getReliefVehicleTypes } from 'utils/reliefVehicleTypes'
import { useForm, Controller, FormProvider } from 'react-hook-form'

import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { Checkbox, FormControlLabel } from '@mui/material'
import ErrorIcon from '@mui/icons-material/Close'
import DatePicker from 'frame/components/date-picker-v5'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import { useTheme } from '@mui/material/styles'

import DetailsInfoWrapper from 'frame/components/details-info-wrapper'
import CardContent from 'frame/components/card-content'
import MuiTextfield from 'frame/components/mui-textfield'
import MuiFormSelect from 'frame/components/mui-form-select'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { zodResolver } from '@hookform/resolvers/zod'
import { schema } from '../new/form-schema'
import { getReservationReasons } from 'utils/reservationUtils'

const Form = ({
  relatedContract,
  disabled,
  error,
  fetchContractStatus,
  fleetIds,
  onClose,
  onFetchContract,
  onSubmit,
  onLookup,
  errorMessage,
  userRoles
}) => {

  const theme = useTheme()
  const [contract, setContract] = useState({})
  const [vehicle, setVehicle] = useState()
  const [values, setValues] = useState(createDefaultModel())
  const [selectedRequestType, setSelectedRequestType] = useState('')
  const [open, setOpen] = useState(true)
  const userSelectedFleets = useSelectedFleets()
  const disableButtons = hasRoles(['fleetcoordinator'], userRoles)
  const lookupContractData = relatedContract?.[vehicle?.agreementNo] || null
  const locked = disabled || ! contract?.agreementNo
  const hasContract = !!contract?.agreementNo

  const fleetOptions = userSelectedFleets.map(item => ({
    label: item.label,
    value: item.value,
  }))

  const handleClose = () => {
    setOpen(false)
  }

  const methods = useForm({
    resolver: zodResolver(schema),
    reValidateMode: 'all',
    defaultValues: {
      pickupAddress: values?.pickupAddress ?? '',
      vehicleType: values?.vehicleType ?? '',
      collectionDate: values?.collectionDate ?? null,
      bookedReturnDate: values?.bookedReturnDate ?? null,
      contactName: values?.contactName ?? '',
      contactEmailAddress: values?.contactEmailAddress ?? '',
      contactMobileNumber: values.contactMobileNumber ?? '',
    }
  })

  const onRegoChange = (option) => {
    setContract({})
    setVehicle(option)
    if (option?.agreementNo) {
      onFetchContract(option.agreementNo, fleetIds)
    }
  }

  const onInputChange = ({ target: { id, name, value } }) => {
    setValues({ ...values, [id || name]: value })
    methods.setValue(name, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const onSelectChange = (key, value) => {
    setValues({
      ...values,
      [key]: value,
    })
    methods.setValue(key, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const handleRequestTypeChange = (event) => {
    setSelectedRequestType(event.target.name)
    setValues({ ...values, 'requestType': event.target.name })
    if (selectedRequestType === 'Rental') {
      setOpen(true)
    }
  }

  const onFleetChange = (value) => {
    const fleet = userSelectedFleets.filter(f => f.value === value)[0]
    const accountName = fleet ? fleet.label : null
    setValues(prevValues => ({
      ...prevValues,
      accountName,
      thirdPartyNumber: value ?? '',
    }))
  }

  const onSubmitForm = () => {
    if (values.requestType
        && values.collectionDate
        && values.bookedReturnDate
        && values.vehicleType
        && values.pickupAddress
        && values.contactName
        && values.contactEmailAddress
        && values.contactMobileNumber
    ) {
      onSubmit(values)
    }
  }
  let vehicleTypes = getReliefVehicleTypes()
  let reservationReasons = getReservationReasons()

  const getMaxReturnDate = () => {
    if (hasContract && contract.maturityDate) {
      return moment(contract.maturityDate)
    }
    return null
  }

  // Similar to getDerivedStateFromProps
  // We need to update local state here once we receive
  // the corresponding contract.
  // So we set the contract and add appropriate contract values to the
  // relief vehicle request as is needed.
  useEffect(() => {
    if (lookupContractData?.agreementNo) {
      console.log('[lookupContractData]', { vehicle, lookupContractData })
      setContract(lookupContractData)
      setValues({
        ...values,
        driver: lookupContractData.driver || '',
        driverMobile: lookupContractData.driverMobile || '',
        driverEmail: lookupContractData.driverEmail || '',
        agreementNo: lookupContractData.agreementNo || '',
        thirdPartyNumber: lookupContractData.thirdPartyNumber || '',
        registration: lookupContractData.registration || '',
      })
    }
  }, [lookupContractData]) // eslint-disable-line

  const expiredMaturityDate = moment(contract.maturityDate) <= moment()

  // to ensure description are in same cases
  const correctCases = (str) =>
    capitalize(str.trim().toLowerCase())

  const isFieldDisabled = () => {
    if (selectedRequestType === 'Relief') {
      return (
        isDisabled() ||
        !values?.reservationReason ||
        values?.reservationReason === 'Service'
      )
    }
    return isDisabled()
  }

  const isDisabled = () => {
    if (selectedRequestType === 'Relief') {
      return locked || expiredMaturityDate
    } else if (selectedRequestType === 'Rental') {
      return !values?.thirdPartyNumber
    }
    return true
  }

  const formIncomplete =
  !values.vehicleType ||
  !values.pickupAddress ||
  !values.collectionDate ||
  !values.bookedReturnDate ||
  !values.contactName ||
  !values.contactEmailAddress ||
  !values.contactMobileNumber ||
  !values.reservationReason ||
  values.reservationReason === 'Service' ||
  (values.reservationReason === 'Other' && !values.Reason)


  const isSubmitDisabled = () => {
    if (selectedRequestType === 'Relief') {
      return formIncomplete || isDisabled()
    }
    return (
      isDisabled()
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormProvider>
        <form
          id="request-relief-vehicle-form"
          onSubmit={methods.handleSubmit(onSubmitForm)}>
          <CardContent>
            {error && (
              <Stack gap={1}>
                <Alert>
                  {errorMessage || 'Something went wrong while trying to submit relief vehicle request.' }
                </Alert>
              </Stack>
            )}

            <Stack gap={4}>
              <Stack gap={2}>
                <Typography variant="h4" fontWeight={600}>
                  What do you need the vehicle for?
                </Typography>
                <Stack flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
                  <Stack width="50%">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedRequestType === 'Relief'}
                          onChange={handleRequestTypeChange}
                          name="Relief"
                          color="default"
                          sx={{
                            '&.Mui-checked': {
                              color: '#e91e25',
                            },
                          }}/>
                      }
                      label="Relief Vehicle"/>
                    <Box display="block" mt={-1}>
                      <Typography color="#8b909b" fontSize={14} ml={4}>
                        A replacement vehicle whilst your vehicle is off the road
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack width="50%">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedRequestType === 'Rental'}
                          onChange={handleRequestTypeChange}
                          name="Rental"
                          color="default"
                          sx={{
                            '&.Mui-checked': {
                              color: '#e91e25',
                            },
                          }}/>
                      }
                      label="Rental Vehicle"/>
                    <Box display="block" mt={-1}>
                      <Typography color="#8b909b" fontSize={14} ml={4}>
                        An additional vehicle to use
                      </Typography>
                    </Box>
                    {selectedRequestType === 'Rental' && (
                      <>
                        <Box display="flex" alignItems="left" mt={2}>
                          <ErrorIcon
                            className="error"
                            color="error"
                            size={30} />
                          <Typography color="error" fontSize={12} ml={1}>
                            Warning: Costs for the Rental vehicle will be oncharged to you.
                          </Typography>
                        </Box>
                        <Dialog open={open}>
                          <DialogContent>
                            <Stack flexDirection="column" gap={1}>
                              <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h4">
                                  Warning:
                                </Typography>
                              </Stack>
                              <Typography variant="h5">
                                Costs for the Rental vehicle will be oncharged to you.
                              </Typography>
                              <Button
                                fullWidth
                                color="primary"
                                variant="contained"
                                onClick={handleClose} >
                                Agree
                              </Button>
                            </Stack>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                  </Stack>
                </Stack>
              </Stack>
              {selectedRequestType === 'Relief' && (
                <>
                  <Stack gap={2}>
                    <Typography variant="h4" fontWeight={600}>Vehicle
                    </Typography>
                    <Typography variant="bodyLarge">
                  Please select the vehicle you wish to book a relief vehicle for.
                    </Typography>
                  </Stack>
                  <Stack flex={1} gap={1}>
                    <RegistrationLookupWrapper
                      lookupContractData={contract}
                      fetchContractStatus={fetchContractStatus}>
                      <LookupField
                        id="registration"
                        disabled={disabled || disableButtons}
                        fullWidth
                        label="Vehicle Registration"
                        lookupPath="vehicleregistration"
                        value={vehicle}
                        variant="outlined"
                        validators={['required']}
                        errorMessages={['This field is required']}
                        onChange={onRegoChange}
                        onLookup={onLookup}
                        resourceModel="requestReliefVehicles"/>
                    </RegistrationLookupWrapper>
                  </Stack>
                </>
              )}
              {selectedRequestType === 'Rental' && (
                <Stack gap={2}>
                  <Typography variant="h4" fontWeight={600}>
                    Fleet
                  </Typography>
                  <Typography variant="bodyLarge">
                    Please select the Fleet you wish to book a vehicle for.
                  </Typography>
                  <Stack flex={1} gap={1} width="50%">
                    <Controller
                      id="thirdPartyNumber"
                      name="thirdPartyNumber"
                      control={methods.control}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          options={fleetOptions}
                          getOptionLabel={option => option.label || ''}
                          value={fleetOptions.find(option => option.value === values.thirdPartyNumber) || null}
                          onChange={(event, value) => {
                            onFleetChange(value ? value.value : '')
                          }}
                          renderInput={params => (
                            <MuiTextfield
                              {...params}
                              label="Fleet"
                              value={values.thirdPartyNumber || ''}/>
                          )}/>
                      )}/>
                  </Stack>
                </Stack>
              )}
              {hasContract && selectedRequestType === 'Relief' && (
                <Stack gap={1}>
                  <DetailsInfoWrapper
                    dataId="relief-vehicle-booking"
                    items={[{
                      label: 'Registration',
                      value: contract.registration || '-'
                    },
                    {
                      label: 'Description',
                      value: contract.make && contract.model ?
                        correctCases(contract.make) + ' ' + correctCases(contract.model)
                        : '-',
                    },
                    {
                      label: 'Cost Centre',
                      value: contract.costCentre || '-',
                    },
                    {
                      label: 'Maturity Date',
                      value: contract.maturityDate || '-',
                      format: 'Do MMMM YYYY',
                    },
                    {
                      label: 'Driver',
                      value: contract.driver || '-',
                    },
                    {
                      label: 'Controller',
                      value: contract.controller || '-',
                    },
                    ]}/>
                </Stack>
              )}
              <Stack gap={2}>
                <Typography variant="h4" fontWeight={600}>
                  Booking details
                </Typography>
                <Typography variant="bodyLarge">
                  Please complete all required fields.
                </Typography>
                <Stack flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
                  <Stack width="50%">
                    <Controller
                      name="vehicleType"
                      control={methods.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <MuiFormSelect
                          {...field}
                          id="vehicleType"
                          disabled={isDisabled()}
                          label="Vehicle Type*"
                          labelId="vehicleType-label"
                          options={vehicleTypes}
                          value={values.vehicleType || ''}
                          onChange={(event) =>
                            onSelectChange('vehicleType', event?.target?.value)
                          }/>
                      )}/>
                    <Typography color="error" fontSize={12}>
                      {methods.formState.errors?.vehicleType?.message}
                    </Typography>
                  </Stack>
                  <Stack width="50%">
                    <Controller
                      name="pickupAddress"
                      control={methods.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <MuiTextfield
                          {...field}
                          id="pickupAddress"
                          name="pickupAddress"
                          disabled={isDisabled()}
                          error={Boolean(methods.formState.errors?.pickupAddress?.message)}
                          label="Pickup Address*"
                          value={values.pickupAddress || ''}
                          onChange={onInputChange}/>
                      )}/>
                    <Typography color="error" fontSize={12}>
                      {methods.formState.errors?.pickupAddress?.message}
                    </Typography>
                  </Stack>
                </Stack>
                {selectedRequestType === 'Relief' && (
                  <Stack flexDirection={{ xs: 'column', md: 'row' }} alignItems="center" gap={1}>
                    {/* Left: Reservation Reason dropdown */}
                    <Stack width={{ xs: '100%', md: '50%' }}>
                      <Controller
                        name="reservationReason"
                        control={methods.control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <MuiFormSelect
                            {...field}
                            id="reservationReason"
                            disabled={isDisabled()}
                            label="Reservation Reason*"
                            labelId="reservationReason-label"
                            options={reservationReasons}
                            value={values.reservationReason || ''}
                            onChange={(event) =>
                              onSelectChange('reservationReason', event?.target?.value)
                            }/>
                        )}/>
                      <Typography color="error" fontSize={12}>
                        {methods.formState.errors?.reservationReason?.message}
                      </Typography>
                    </Stack>

                    {/* Right: Inline error text or Reason field */}
                    <Stack
                      width={{ xs: '100%', md: '50%' }}>
                      {values.reservationReason === 'Service' && (
                        <Stack direction="row" alignItems="center">
                          <ErrorIcon
                            style={{
                              fill: 'rgb(229, 15, 15)',
                              fontSize: '1.5rem',
                              marginRight: 6,
                              verticalAlign: 'middle',
                            }}/>
                          <Typography color="error" fontSize={12}>
                              A relief vehicle cannot be booked for this reason, and we recommend you
                              request a courtesy car from the supplier.
                          </Typography>
                        </Stack>
                      )}
                      {/* "Other" selection Reason field */}
                      {values.reservationReason === 'Other' && (
                        <>
                          <Controller
                            name="Reason"
                            control={methods.control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <MuiTextfield
                                {...field}
                                id="Reason"
                                name="Reason"
                                disabled={isDisabled()}
                                error={Boolean(methods.formState.errors?.Reason?.message)}
                                label="Reason*"
                                value={values.Reason || ''}
                                onChange={onInputChange}/>
                            )}/>
                          <Typography color="error" fontSize={12}>
                            {methods.formState.errors?.Reason?.message}
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </Stack>
                )}
                <Stack flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
                  <Stack width="50%">
                    <DatePicker
                      label="Start Date*"
                      format="DD/MM/YYYY"
                      minDate={dayjs()}
                      maxDate={values.bookedReturnDate ? dayjs(values.bookedReturnDate) : getMaxReturnDate()}
                      value={values?.collectionDate ?? null}
                      disabled={isFieldDisabled()}
                      onChange={(value) => {
                        const start = value
                        const end = values.bookedReturnDate

                        if (end && dayjs(start).isAfter(end)) {
                          methods.setValue('bookedReturnDate', dayjs(start).toDate(), {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }

                        setValues({
                          ...values,
                          collectionDate: value,
                        })

                        methods.setValue('collectionDate', dayjs(start).toDate(), {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }}/>
                    <Typography color="error" fontSize={12}>
                      {methods.formState.errors?.collectionDate?.message}
                    </Typography>
                  </Stack>
                  <Stack width="50%">
                    <DatePicker
                      label="End Date*"
                      format="DD/MM/YYYY"
                      minDate={values.collectionDate ? dayjs(values.collectionDate) : dayjs()}
                      maxDate={getMaxReturnDate()}
                      value={values?.bookedReturnDate ?? null}
                      disabled={isFieldDisabled()}
                      onChange={(value) => {
                        setValues({
                          ...values,
                          bookedReturnDate: value,
                        })
                        methods.setValue('bookedReturnDate', dayjs(value).toDate(), {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }}/>
                    <Typography color="error" fontSize={12}>
                      {methods.formState.errors?.bookedReturnDate?.message}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
                  <Stack width="50%">
                    <Controller
                      name="contactName"
                      control={methods.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <MuiTextfield
                          {...field}
                          id="contactName"
                          name="contactName"
                          disabled={isFieldDisabled()}
                          error={Boolean(methods.formState.errors?.contactName?.message)}
                          label="Contact Name*"
                          value={values.contactName || ''}
                          onChange={onInputChange}/>
                      )}/>
                    <Typography color="error" fontSize={12}>
                      {methods.formState.errors?.contactName?.message}
                    </Typography>
                  </Stack>
                  <Stack width="50%">
                    <Controller
                      name="contactEmailAddress"
                      control={methods.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <MuiTextfield
                          {...field}
                          id="contactEmailAddress"
                          name="contactEmailAddress"
                          disabled={isFieldDisabled()}
                          error={Boolean(methods.formState.errors?.contactEmailAddress?.message)}
                          label="Contact Email Address*"
                          value={values.contactEmailAddress || ''}
                          onChange={onInputChange}/>
                      )}/>
                    <Typography color="error" fontSize={12}>
                      {methods.formState.errors?.contactEmailAddress?.message}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
                  <Stack width="50%">
                    <Controller
                      name="contactMobileNumber"
                      control={methods.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <MuiTextfield
                          {...field}
                          id="contactMobileNumber"
                          name="contactMobileNumber"
                          disabled={isFieldDisabled()}
                          error={Boolean(methods.formState.errors?.contactMobileNumber?.message)}
                          label="Contact Mobile Number*"
                          value={values.contactMobileNumber || ''}
                          onChange={onInputChange}/>
                      )}/>
                    <Typography color="error" fontSize={12}>
                      {methods.formState.errors?.contactMobileNumber?.message}
                    </Typography>
                  </Stack>
                  <Stack width="50%">
                  </Stack>
                </Stack>
                <Stack gap={1} flex={1}>
                  <Controller
                    name="comments"
                    control={methods.control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <>
                        <MuiTextfield
                          {...field}
                          multiline
                          rows={10}
                          id="comments"
                          disabled={isFieldDisabled()}
                          error={Boolean(methods.formState.errors?.comments?.message)}
                          label="Comments"
                          value={values.comments || ''}
                          onChange={onInputChange}/>
                      </>
                    )}/>
                </Stack>
              </Stack>
            </Stack>

            <Divider sx={{ mt: 3, mb: 2, borderColor: theme.palette.accent.sky }} />
            <Stack
              flexDirection={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              rowGap={2}>
              <Box width={{ xs: '100%', md: '205px' }}>
                <Button
                  fullWidth
                  color="error"
                  variant="outlined"
                  onClick={onClose}>
                  Close
                </Button>
              </Box>
              <Stack flexDirection="row" gap={1}>

                <Box width={{ xs: '100%', md: '205px' }}>
                  <Button
                    fullWidth
                    color="error"
                    variant="outlined"
                    disabled={isSubmitDisabled()}
                    onClick={onClose}>
                    Cancel
                  </Button>
                </Box>
                <Box width={{ xs: '100%', md: '205px' }}>
                  <Button
                    type="submit"
                    fullWidth
                    color="primary"
                    disabled={isSubmitDisabled()}
                    variant="contained">
                    Request Relief/Rental Vehicle
                  </Button>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </form>
      </FormProvider>
    </LocalizationProvider>
  )
}

Form.propTypes = {
  relatedContract: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  fetchContractStatus: PropTypes.string,
  fleetIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onFetchContract: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onLookup: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,

}

export default Form
